// Minimal, safe markdown renderer. Builds React elements (strings auto-escape),
// never injects HTML. Covers the subset the deliverable templates emit:
// headings, paragraphs, bullets/numbered lists, tables, blockquotes, code
// fences, checkboxes, bold/italic/inline-code/links, hr.

import { Fragment, type ReactNode } from 'react';

function renderInline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  // Tokenize: links, bold, inline code.
  const re = /(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*([^*]+)\*\*)|(`([^`]+)`)|(_([^_]+)_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) {
      out.push(
        <a key={`${keyBase}-l${i}`} href={m[3]} target="_blank" rel="noopener noreferrer" className="text-brand-300 underline hover:text-brand-200">
          {m[2]}
        </a>,
      );
    } else if (m[4]) {
      out.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-slate-50">{m[5]}</strong>);
    } else if (m[6]) {
      out.push(<code key={`${keyBase}-c${i}`} className="rounded bg-slate-800 px-1 py-0.5 text-[0.85em] text-brand-300">{m[7]}</code>);
    } else if (m[8]) {
      out.push(<em key={`${keyBase}-i${i}`} className="italic text-slate-300">{m[9]}</em>);
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ content }: { content: string }) {
  const lines = content.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (line.trim() === '') { i++; continue; }

    // Code fence
    if (line.startsWith('```')) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').startsWith('```')) {
        buf.push(lines[i] ?? '');
        i++;
      }
      i++; // closing fence
      blocks.push(
        <pre key={key++} className="my-3 overflow-x-auto rounded-lg border border-slate-800 bg-slate-900 p-3 text-xs text-slate-300">
          {buf.join('\n')}
        </pre>,
      );
      continue;
    }

    // Headings
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const level = h[1]!.length;
      const cls =
        level === 1 ? 'mt-2 mb-3 text-2xl font-bold text-slate-50'
        : level === 2 ? 'mt-6 mb-2 text-lg font-bold text-brand-300'
        : 'mt-4 mb-1.5 text-base font-semibold text-slate-100';
      blocks.push(<div key={key++} className={cls}>{renderInline(h[2] ?? '', `h${key}`)}</div>);
      i++;
      continue;
    }

    // Horizontal rule
    if (/^-{3,}\s*$/.test(line)) {
      blocks.push(<hr key={key++} className="my-4 border-slate-800" />);
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('>')) {
      const buf: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('>')) {
        buf.push((lines[i] ?? '').replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(
        <blockquote key={key++} className="my-3 border-l-2 border-brand-600 bg-slate-900/50 px-4 py-2 text-sm text-slate-300">
          {buf.map((b, bi) => <div key={bi}>{renderInline(b, `q${key}-${bi}`)}</div>)}
        </blockquote>,
      );
      continue;
    }

    // Table
    if (line.trimStart().startsWith('|')) {
      const tbl: string[] = [];
      while (i < lines.length && (lines[i] ?? '').trimStart().startsWith('|')) {
        tbl.push(lines[i] ?? '');
        i++;
      }
      const parseRow = (r: string) => r.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
      const header = parseRow(tbl[0] ?? '');
      const body = tbl.slice(1).filter((r) => !/^\s*\|?[\s:|-]+\|?\s*$/.test(r)).map(parseRow);
      blocks.push(
        <div key={key++} className="my-3 overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900">
              <tr>{header.map((c, ci) => <th key={ci} className="th">{renderInline(c, `th${key}-${ci}`)}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {body.map((row, ri) => (
                <tr key={ri} className="bg-slate-950/40">
                  {row.map((c, ci) => <td key={ci} className="td">{renderInline(c, `td${key}-${ri}-${ci}`)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Lists (unordered, checkbox, ordered)
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const items: Array<{ text: string; checked?: boolean }> = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i] ?? '')) {
        let t = (lines[i] ?? '').replace(/^\s*([-*]|\d+\.)\s+/, '');
        let checked: boolean | undefined;
        const cb = t.match(/^\[( |x)\]\s+(.*)$/i);
        if (cb) { checked = cb[1]!.toLowerCase() === 'x'; t = cb[2] ?? ''; }
        items.push({ text: t, checked });
        i++;
      }
      blocks.push(
        <ul key={key++} className="my-2 space-y-1 pl-1 text-sm text-slate-200">
          {items.map((it, ii) => (
            <li key={ii} className="flex gap-2">
              <span className="mt-0.5 shrink-0 text-brand-400">{it.checked === undefined ? '•' : it.checked ? '☑' : '☐'}</span>
              <span>{renderInline(it.text, `li${key}-${ii}`)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Paragraph (collect consecutive plain lines)
    const buf: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() !== '' &&
      !/^(#{1,4}\s|>|\||```|\s*([-*]|\d+\.)\s|-{3,}\s*$)/.test(lines[i] ?? '')
    ) {
      buf.push(lines[i] ?? '');
      i++;
    }
    blocks.push(
      <p key={key++} className="my-2 text-sm leading-relaxed text-slate-200">
        {buf.map((b, bi) => (
          <Fragment key={bi}>
            {renderInline(b, `p${key}-${bi}`)}
            {bi < buf.length - 1 ? ' ' : null}
          </Fragment>
        ))}
      </p>,
    );
  }

  return <div>{blocks}</div>;
}
